import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// eslint-disable-next-line import/no-unresolved
import { useCompletion } from 'ai/react';
import {
  Box,
  IconButton,
  Tooltip,
  useTheme,
  TextField,
  InputAdornment,
  Paper,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Menu,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {
  Copy,
  Search,
  Check,
  Languages,
  Pencil,
  Save,
  Eye,
  EyeOff,
} from 'lucide-mui';
import { translateText, top50Languages, LanguageCode } from '../../translate';

import { RootState } from '../../../../reducers';
import templates from './templates';
import Empty from './Empty';
import LimitReached from './LimitReached';
import { getToken } from '../../../../lib/localStorageHandler';
import { useLimits } from '../../../plan/hooks/limits';
import config from '../../../../../config';
import { FileType, Segment } from '../../../../types';
import FadeStaggerCircles from '../../../../assets/icons/fade-stagger-circles.svg';
import {
  setTemplateOutput,
  deleteTemplateOutput,
} from '../../selectedVideoSlice';
import { getLanguageName } from '../../../library/files/FilesEmpty/supportedLanguages';
import TemplateEditor from './MDXEditor';
import { saveTemplateOutput } from '../../../../services/fileSystemService';
import DownloadFile from '../Download';
import {
  selectAllTemplates,
  selectTemplatesStatus,
  fetchTemplates,
} from '../../templateSlice';

interface ToolbarButton {
  title: string;
  icon: React.ReactNode;
  onClick: (event?: React.MouseEvent<HTMLButtonElement>) => void;
  component?: React.ReactNode;
}

const stripCitations = (text: string): string => {
  if (!text) return '';
  // Matches [→](number) pattern and any whitespace before it
  return text.replace(/\s*\[→\]\(\d+(\.\d+)?\)/g, '');
};

function getFullPromptText(
  prompt: string,
  file: FileType,
  transcript: {
    compactSegments: Segment[];
    selectedSpeakerIds: string[];
  },
  language: string
) {
  return `
<directions>
${prompt}
</directions>
<rules>
Follow these rules:
- First of all, the ${language} language should be used, and this is important.
- Always Use Markdown to format your response for things like titles and lists.
- Jump right into the content. Do not preface your response with "Here is the transcript..." or anything similar above the title.
- Do not have a closing with something like 'overall'
- Do not end with a signature or a closing.
- Ensure no significant stretch of the transcript is glossed over
- Fix any transcript issues

Citation and Timestamp Rules:
- Citations MUST use the format: [→](seconds)
  * Correct: [→](42)
  * Incorrect: [→](Meeting Recording@42)
- VERIFY all timestamps before citing them
- Ensure timestamps are logically sequential
- Check that cited moments actually contain relevant content
- Most recordings are 15-90 minutes
- Timestamps > 7200 (2 hours) are suspicious
- Timestamps > 14400 (4 hours) are likely errors

Speaker Identification:
- NEVER use labels like "SPEAKER_01", "SPEAKER_1", "Speaker A", etc.
- Instead, identify speakers by:
  * Role and name if known: "Product Manager Sarah"
  * Role if name unknown: "Product Manager"
  * Descriptive characteristics if role unknown:
    - "Female Customer"
    - "Male Engineer"
    - "Senior Team Member"
- Be consistent with speaker identification throughout
- If multiple speakers share a role, differentiate them clearly

Formatting:
- Use single # for main headers
- Use ## for subheaders
- Never include additional # symbols before section headers
- For timestamps:
  * Always use the exact timestamp from the transcript segments
  * Format all timestamps with square brackets like [00:00:00]
  * Never modify or estimate timestamps - use only the exact times
  * Ensure each quote has its corresponding timestamp
  * The timestamp should always precede the quote it refers to
</rules>
<speakermap>${JSON.stringify(file?.textData?.speakers || {})}</speakermap>
<customvocab></customvocab>
<transcript>${JSON.stringify(transcript.compactSegments)}</transcript>`;
}

interface TemplatesProps {
  templateId: string;
  customPrompt?: string;
}

const Templates: React.FC<TemplatesProps> = ({ templateId, customPrompt }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const {
    transcript,
    file,
    postProcess: { userPromptResponses = {} } = {},
  } = useSelector((state: RootState) => state.selectedVideo);
  const { defaultTranscriptionLanguage } = useSelector((state: RootState) => ({
    defaultTranscriptionLanguage:
      state.settings.recorder.otherSettings.defaultTranscriptionLanguage,
  }));
  const [prompt, setPrompt] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const guardLimits = useLimits();
  const showLimitReached = guardLimits('askAI') <= 0;
  const transcriptLanguage = getLanguageName(
    defaultTranscriptionLanguage || 'en'
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [
    translationAnchorEl,
    setTranslationAnchorEl,
  ] = useState<null | HTMLElement>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [hideReferences, setHideReferences] = useState(false);
  const savedTemplates = useSelector(selectAllTemplates);
  const templatesStatus = useSelector(selectTemplatesStatus);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;
  const currentTemplateRef = useRef<{
    id: string;
    title: string;
    prompt: string;
  } | null>(null);

  useEffect(() => {
    const template =
      templates.find((t) => t.id === templateId) ||
      savedTemplates.find((t) => t.id === templateId);
    if (template) {
      currentTemplateRef.current = template;
    }
  }, [templateId, savedTemplates]);

  const {
    complete,
    completion: templateResponse,
    error: completionError,
    isLoading,
  } = useCompletion({
    api: `${config.authBaseUrlV2}/proxy/ai/completion`,
    streamProtocol: 'text',
    headers: {
      Authorization: `Bearer ${getToken()}`,
    },
    initialCompletion: userPromptResponses[templateId]?.responseText,
    onFinish: (_, completion) => {
      const currentTemplate = currentTemplateRef.current;
      if (!currentTemplate || currentTemplate.prompt !== prompt) {
        return;
      }

      // Only save if we have a complete response
      if (!completion || completion.trim().endsWith('...')) {
        console.error('Incomplete response received');
        return;
      }

      dispatch(
        setTemplateOutput({
          templateId,
          responseText: completion,
          promptTitle: currentTemplate.title,
        })
      );
    },
  });

  useEffect(() => {
    if (templatesStatus === 'idle') {
      dispatch(fetchTemplates());
    } else if (templatesStatus === 'failed' && retryCount < MAX_RETRIES) {
      const retryDelay = Math.min(1000 * 2 ** retryCount, 5000);
      setTimeout(() => {
        setRetryCount((prev) => prev + 1);
        dispatch(fetchTemplates());
      }, retryDelay);
    }
  }, [dispatch, templatesStatus, retryCount]);

  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (templateId === 'custom' && customPrompt) {
      setPrompt(customPrompt);
    } else {
      const template =
        templates.find((t) => t.id === templateId) ||
        savedTemplates.find((t) => t.id === templateId);
      if (template) {
        setPrompt(template.prompt);
      } else if (templatesStatus !== 'loading') {
        const defaultTemplate = templates[0];
        setPrompt(defaultTemplate.prompt);
      }
    }
  }, [templateId, customPrompt, savedTemplates, templatesStatus]);

  const fetchTemplateResponse = async () => {
    if (
      !!templateResponse ||
      !prompt ||
      !transcript.compactSegments ||
      transcript.compactSegments.length === 0
    ) {
      return;
    }

    const currentTemplate = currentTemplateRef.current;
    if (!currentTemplate || currentTemplate.prompt !== prompt) {
      return;
    }

    const promptText = getFullPromptText(
      currentTemplate.prompt,
      file,
      transcript,
      transcriptLanguage
    );

    try {
      await complete(promptText, {
        body: {
          model: 'fast',
          templateId: currentTemplate.id,
          promptTitle: currentTemplate.title,
          template: {
            id: currentTemplate.id,
            title: currentTemplate.title,
            prompt: currentTemplate.prompt,
          },
          feature: 'templates',
          args: {
            id: currentTemplate.id,
            title: currentTemplate.title,
            fileId: file?._id,
            teamId: file?.teamId,
            ownerId: file?.ownerId,
          },
          maxTokens: 4000, // Ensure we have enough tokens for complete responses
          temperature: 0.7, // Add some variability but keep it focused
          stream: true, // Ensure streaming is explicitly enabled
        },
      });
    } catch (error) {
      console.error('Error during completion:', error);
    }
  };

  useEffect(() => {
    fetchTemplateResponse();
  }, [prompt, transcript, file, templateResponse]);

  const handleCopyResponse = () => {
    if (templateResponse) {
      const cleanText = stripCitations(templateResponse);
      const tempElement = document.createElement('div');
      tempElement.innerHTML = cleanText;
      const formattedText = tempElement.innerText;

      navigator.clipboard
        .writeText(formattedText)
        .then(() => {
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 2000);
        })
        .catch((err) => console.error('Failed to copy text: ', err));
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleteDialogOpen(false);
    await dispatch(deleteTemplateOutput({ fileId: file?._id, templateId }));
  };

  const handleTranslateClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setTranslationAnchorEl(event.currentTarget);
  };

  const handleTranslateClose = () => {
    setTranslationAnchorEl(null);
  };

  const handleTranslateContent = async (targetLang: LanguageCode) => {
    setIsTranslating(true);
    try {
      const translated = await translateText({
        text: templateResponse || '',
        targetLang,
      });
      dispatch(
        setTemplateOutput({
          templateId,
          responseText: translated,
          promptTitle: userPromptResponses[templateId]?.promptTitle,
        })
      );
    } catch (err) {
      console.error('Translation error:', err);
    } finally {
      setIsTranslating(false);
      handleTranslateClose();
    }
  };

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
  };

  useEffect(() => {
    if (templateResponse) {
      setEditedContent(templateResponse);
    }
  }, [templateResponse]);

  const handleSave = async () => {
    if (!file?._id || !templateId) return;

    setIsSaving(true);
    try {
      await saveTemplateOutput({
        fileId: file._id,
        templateId,
        responseText: editedContent,
      });

      dispatch(
        setTemplateOutput({
          templateId,
          responseText: editedContent,
          promptTitle: userPromptResponses[templateId]?.promptTitle,
        })
      );

      setIsEditing(false);
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Failed to save template output:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const getEditIcon = () => {
    if (isEditing) {
      return isSaving ? <CircularProgress size={18} /> : <Save />;
    }
    return <Pencil />;
  };

  const toolbarButtons: ToolbarButton[] = [
    {
      title: 'Translate',
      icon: <Languages />,
      onClick: handleTranslateClick,
    },
    {
      title: isEditing ? 'Save' : 'Edit',
      icon: getEditIcon(),
      onClick: isEditing ? handleSave : () => setIsEditing(true),
    },
    {
      title: `${hideReferences ? 'Show' : 'Hide'} references`,
      icon: (
        <IconButton
          size="small"
          onClick={() => setHideReferences(!hideReferences)}
          sx={{
            color: hideReferences ? 'text.disabled' : 'text.secondary',
            borderRadius: '4px',
            width: '30px',
            height: '30px',
            padding: '6px',
            '&:hover': {
              color: hideReferences ? 'text.secondary' : 'text.primary',
            },
          }}
        >
          {hideReferences ? (
            <EyeOff fontSize="small" />
          ) : (
            <Eye fontSize="small" />
          )}
        </IconButton>
      ),
      onClick: () => {}, // Handled by the IconButton
    },
    {
      title: 'Download',
      icon: (
        <DownloadFile
          text={stripCitations(templateResponse || '')}
          fileName={file?.name || 'template-output'}
          variant="icon"
        />
      ),
      onClick: () => {}, // Click is handled by DownloadFile component
    },
    {
      title: 'Copy Response',
      icon: copySuccess ? <Check /> : <Copy />,
      onClick: handleCopyResponse,
    },
  ];

  const renderEditorContent = () => {
    if (
      !transcript.compactSegments ||
      transcript.compactSegments.length === 0
    ) {
      return <Empty templateId={templateId} />;
    }

    if (showLimitReached) {
      return <LimitReached />;
    }

    if (isLoading) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <img
            src={FadeStaggerCircles}
            alt="Loading..."
            style={{ width: '48px', height: '48px' }}
          />
        </Box>
      );
    }

    if (completionError) {
      return (
        <Typography color="error">
          Error generating response. Please try again.
        </Typography>
      );
    }

    return (
      <Box sx={{ position: 'relative', height: '100%' }}>
        {isEditing ? (
          <TemplateEditor content={editedContent} onChange={setEditedContent} />
        ) : (
          <Box
            sx={{
              p: 2,
              height: '100%',
              overflowY: 'auto',
              backgroundColor: theme.palette.background.paper,
            }}
          >
            {templateResponse && (
              <Box>
                <TemplateEditor
                  content={templateResponse}
                  readOnly
                  searchTerm={searchTerm}
                  hideReferences={hideReferences}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: theme.spacing(1),
          marginBottom: theme.spacing(1),
        }}
      >
        <Paper
          elevation={0}
          component="form"
          onSubmit={handleSearch}
          sx={{
            display: 'flex',
            flexGrow: 1,
            backgroundColor: theme.palette.background.default,
            borderRadius: theme.spacing(1),
            height: '40px',
            border: '1px solid',
            borderColor: 'divider',
            '& .MuiOutlinedInput-root': {
              height: '100%',
              '& fieldset': {
                borderColor: 'transparent',
              },
              '&:hover fieldset': {
                borderColor: 'transparent',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'transparent',
              },
              '& .MuiInputAdornment-root': {
                '& .MuiSvgIcon-root': {
                  fontSize: '20px',
                  color: 'text.secondary',
                  transition: 'color 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                },
              },
              '&:hover .MuiInputAdornment-root .MuiSvgIcon-root': {
                color: 'text.primary',
              },
            },
          }}
        >
          <TextField
            placeholder="Search document..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Paper>
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            backgroundColor: theme.palette.background.default,
            borderRadius: theme.spacing(1),
            height: '40px',
            border: '1px solid',
            borderColor: 'divider',
            '& .MuiIconButton-root': {
              width: '28px',
              height: '28px',
              borderRadius: '6px',
              margin: '6px',
              transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                transform: 'translateY(-1px)',
                '& .MuiSvgIcon-root': {
                  transform: 'scale(1.05)',
                },
              },
              '&:active': {
                transform: 'translateY(1px)',
                '& .MuiSvgIcon-root': {
                  transform: 'scale(0.98)',
                },
              },
              '& .MuiSvgIcon-root': {
                fontSize: '18px',
                transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
              },
            },
          }}
        >
          {toolbarButtons.map((button, index) =>
            button.component ? (
              <Box key={index} sx={{ margin: '4px' }}>
                {button.component}
              </Box>
            ) : (
              <Tooltip key={index} title={button.title} placement="top" arrow>
                <IconButton
                  onClick={button.onClick}
                  sx={{
                    flexDirection: 'column',
                    color:
                      button.title.includes('Copy') && copySuccess
                        ? 'success.main'
                        : 'text.primary',
                    '&.Mui-disabled': {
                      opacity: 0.5,
                    },
                  }}
                >
                  {button.icon}
                </IconButton>
              </Tooltip>
            )
          )}
        </Paper>
      </Box>
      <Box sx={{ position: 'relative', height: '100%' }}>
        {renderEditorContent()}
      </Box>
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message="Changes saved successfully"
      />
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <DialogTitle>Are you sure?</DialogTitle>
        <DialogContent>
          <Typography>
            Do you really want to delete this template output?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Menu
        anchorEl={translationAnchorEl}
        open={Boolean(translationAnchorEl)}
        onClose={handleTranslateClose}
      >
        {top50Languages.map((language) => (
          <MenuItem
            key={language.code}
            onClick={() => handleTranslateContent(language.code)}
          >
            {language.name}
          </MenuItem>
        ))}
      </Menu>
      {isTranslating && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          padding={2}
        >
          <CircularProgress size={24} />
        </Box>
      )}
    </Box>
  );
};

export default Templates;
